import { useState } from "react";
import { Link } from "react-router-dom";
import { Send, Loader2, Mail, MessageSquare, CheckCircle2, Clock, Shield, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Seo } from "@/components/Seo";
import { ChatSupportWidget } from "@/components/ChatSupportWidget";
import { TopBar, Footer } from "./Help";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: "general", label: "General enquiry" },
  { value: "support", label: "Support / Bug report" },
  { value: "billing", label: "Billing & promo codes" },
  { value: "partnership", label: "Partnership / Press" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", category: "general", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Name, email and message are required");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thanks — we'll reply within 24 hours");
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send. Try support@sabzy.in.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-background text-foreground grain">
      <Seo
        title="Contact Sabzy AI — Support, feedback, partnerships"
        description="Reach the Sabzy AI team. Bug reports, feature requests, partnership enquiries — we reply within 6 hours on weekdays. Email support@sabzy.in."
        path="/contact"
      />

      {/* BG */}
      <div className="absolute inset-0 -z-10 grid-overlay opacity-15" />
      <div className="absolute left-1/2 top-0 -z-10 h-[460px] w-[820px] -translate-x-1/2 rounded-full bg-primary/10 blur-[140px]" />

      <TopBar />

      {/* HERO */}
      <section className="mx-auto max-w-6xl px-5 pt-14 pb-8 sm:px-8 sm:pt-20 sm:pb-12">
        <div className="label-mono text-primary">[ 03 · Contact ]</div>
        <h1
          className="mt-3 text-balance text-4xl font-light tracking-[-0.035em] sm:text-5xl lg:text-6xl"
          style={{ fontFamily: "'Outfit', sans-serif" }}
        >
          Talk to <span className="copper-text font-medium">us</span>.
        </h1>
        <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-muted-foreground">
          Bug? Feature idea? Partnership? Stuck on a code? We read every message — and a human replies.
        </p>
      </section>

      {/* MAIN */}
      <section className="mx-auto grid max-w-6xl gap-8 px-5 pb-20 sm:px-8 lg:grid-cols-5 lg:gap-12">
        {/* LEFT: aside */}
        <aside className="space-y-4 lg:col-span-2">
          <ContactCard
            icon={Mail}
            title="Email"
            primary={<a href="mailto:support@sabzy.in" className="text-foreground underline-offset-2 hover:underline">support@sabzy.in</a>}
            sub="Avg reply < 6h on weekdays · we read everything"
            testid="contact-card-email"
          />
          <ContactCard
            icon={MessageSquare}
            title="Live chat"
            primary={<span className="text-foreground">Bottom-right bubble · 24/7</span>}
            sub="Hit the bubble on any page to open a thread."
            testid="contact-card-livechat"
          />

          {/* Process timeline */}
          <div className="rounded-2xl border border-border/60 bg-card/40 p-5 backdrop-blur-sm" data-testid="contact-process">
            <div className="mb-3 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">What happens next</div>
            <ol className="space-y-3 text-[13px]">
              <Step n={1} title="Your message arrives" sub="Routed by category to the right team — no auto-replies." />
              <Step n={2} title="A real human replies" sub="Usually within 6 hours on weekdays. We never use canned answers." />
              <Step n={3} title="We close the loop" sub="If it's a bug, we fix it. If it's an idea, we add it to the roadmap." />
            </ol>
          </div>

          <div className="rounded-2xl border border-primary/30 bg-primary/5 p-4 text-[12px] text-muted-foreground" data-testid="contact-account-delete">
            <div className="mb-1 flex items-center gap-2 text-[12px] font-medium text-foreground">
              <Shield className="h-3.5 w-3.5 text-primary" /> Want to delete your account?
            </div>
            Email us from your sign-up address. We purge your data within 24 hours — chats, memory, everything.
          </div>
        </aside>

        {/* RIGHT: form */}
        <div className="lg:col-span-3">
          {done ? (
            <div
              className="relative overflow-hidden rounded-2xl border border-primary/40 bg-gradient-to-br from-primary/10 to-card/40 p-10 text-center"
              data-testid="contact-success"
            >
              <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/60 to-transparent" />
              <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-primary/15 text-primary">
                <CheckCircle2 className="h-7 w-7" />
              </div>
              <h3 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Message received ✓
              </h3>
              <p className="mx-auto mt-2 max-w-sm text-sm text-muted-foreground">
                We&apos;ll reply to <b className="text-foreground">{form.email}</b> shortly. Average wait is under 6 hours on weekdays.
              </p>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                <Button onClick={() => { setDone(false); setForm({ name: "", email: "", subject: "", category: "general", message: "" }); }} variant="outline" data-testid="contact-send-another">
                  Send another
                </Button>
                <Link
                  to="/chat"
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                  data-testid="contact-go-chat"
                >
                  Go back to chat <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>
          ) : (
            <form
              onSubmit={submit}
              className="relative overflow-hidden rounded-2xl border border-border/60 bg-card/50 p-6 backdrop-blur-md sm:p-8"
              data-testid="contact-form"
            >
              <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />

              <div className="mb-5 flex items-center gap-2 text-[11px] uppercase tracking-wider text-muted-foreground">
                <Clock className="h-3 w-3 text-primary" /> avg reply &lt; 6h
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Your name" required>
                  <Input
                    required
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Ayush Ranjan"
                    className="h-11 rounded-xl"
                    data-testid="contact-name"
                  />
                </Field>
                <Field label="Email" required>
                  <Input
                    required
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@domain.com"
                    className="h-11 rounded-xl"
                    data-testid="contact-email"
                  />
                </Field>
              </div>

              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <Field label="Subject">
                  <Input
                    value={form.subject}
                    onChange={update("subject")}
                    placeholder="What's this about?"
                    className="h-11 rounded-xl"
                    data-testid="contact-subject"
                  />
                </Field>
                <Field label="Category">
                  <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                    <SelectTrigger className="h-11 rounded-xl" data-testid="contact-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              <div className="mt-4">
                <Field label="Message" required>
                  <Textarea
                    required
                    rows={6}
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Tell us as much as you can. Steps to reproduce, screenshots (via email is best), and your account email if relevant."
                    className="resize-none rounded-xl"
                    data-testid="contact-message"
                  />
                  <div className="mt-1.5 text-[11px] text-muted-foreground">
                    {form.message.length}/4000
                  </div>
                </Field>
              </div>

              <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
                <div className="text-[11px] text-muted-foreground">
                  By submitting you agree to our{" "}
                  <Link to="/privacy" className="underline-offset-2 hover:text-foreground hover:underline">privacy policy</Link>.
                </div>
                <Button type="submit" disabled={loading} className="group h-11 rounded-xl px-5" data-testid="contact-submit">
                  {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-3.5 w-3.5" />}
                  Send message
                  <ArrowRight className="ml-2 h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
                </Button>
              </div>
            </form>
          )}
        </div>
      </section>

      <Footer />
      <ChatSupportWidget />
    </div>
  );
}

function ContactCard({ icon: Icon, title, primary, sub, testid }) {
  return (
    <div
      className="rounded-2xl border border-border/60 bg-card/40 p-4 backdrop-blur-sm transition hover:border-primary/40"
      data-testid={testid}
    >
      <div className="mb-2 flex items-center gap-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        <Icon className="h-3.5 w-3.5 text-primary" /> {title}
      </div>
      <div className="text-[15px]">{primary}</div>
      {sub && <div className="mt-1.5 text-[11px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[12px] text-muted-foreground">
        {label} {required && <span className="text-primary">*</span>}
      </Label>
      {children}
    </div>
  );
}

function Step({ n, title, sub }) {
  return (
    <li className="flex gap-3">
      <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full border border-primary/40 bg-primary/10 text-[11px] font-semibold text-primary">
        {n}
      </span>
      <div>
        <div className="font-medium text-foreground">{title}</div>
        <div className="text-[12px] leading-relaxed text-muted-foreground">{sub}</div>
      </div>
    </li>
  );
}
